import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRoom } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { getClientId, getStoredName, setStoredName } from "@/lib/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import Lobby from "@/components/Lobby";
import GameRound from "@/components/GameRound";
import GameReveal from "@/components/GameReveal";
import GameOver from "@/components/GameOver";
import PokerRound from "@/components/PokerRound";
import PokerReveal from "@/components/PokerReveal";
import PokerSummary from "@/components/PokerSummary";
import RetroBoard from "@/components/RetroBoard";
import { toast } from "sonner";

const RoomPage = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { room, players, round, submissions, votes, reactions, me, amHost, loading, publishReactionSnapshot } = useRoom(code);
  const [joinName, setJoinName] = useState(getStoredName());
  const [editingName, setEditingName] = useState(false);
  const [joining, setJoining] = useState(false);
  const wasInRoomRef = useRef(false);

  // If the room disappears (host closed it) while we were in it, send everyone home.
  useEffect(() => {
    if (room && me) wasInRoomRef.current = true;
    if (!loading && !room && wasInRoomRef.current) {
      toast("The host closed this room");
      navigate("/");
    }
  }, [room, me, loading, navigate]);

  const doJoin = async (name: string) => {
    if (!room) return;
    // Enforce participant cap (paywall trigger)
    const cap = (room as any).max_players ?? 30;
    if (players.length >= cap) {
      toast.error(`This room is at its ${cap}-player limit. Host can upgrade for larger sessions.`);
      return;
    }
    setJoining(true);
    setStoredName(name);
    // Auto-balance team for game mode
    let team: string | null = null;
    if (room.mode === "game") {
      const purple = players.filter((p) => p.team === "purple").length;
      const green = players.filter((p) => p.team === "green").length;
      team = purple <= green ? "purple" : "green";
    }
    const { error } = await supabase.from("players").insert({
      room_id: room.id,
      client_id: getClientId(),
      name: name.trim(),
      is_host: false,
      team,
    });
    if (error) toast.error("Couldn't join (room may be full)");
    setJoining(false);
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading room…</div>
      </main>
    );
  }

  if (!room) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-4">
        <h1 className="text-3xl font-bold">Room not found</h1>
        <p className="text-muted-foreground">The code "{code}" doesn't match any active room.</p>
        <Button onClick={() => navigate("/")}>Back to home</Button>
      </main>
    );
  }

  // Need to join with a name
  if (!me) {
    const hasStored = !!getStoredName() && !editingName && !!joinName.trim();
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-sm p-6 space-y-4 border-2 shadow-card animate-pop-in">
          <div className="text-center space-y-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Joining room</div>
            <div className="text-3xl font-extrabold tracking-[0.3em]">{room.code}</div>
          </div>
          {hasStored ? (
            <>
              <div className="text-center text-sm text-muted-foreground">Continue as</div>
              <div className="text-center text-2xl font-extrabold">{joinName}</div>
              <Button
                onClick={() => doJoin(joinName.trim())}
                disabled={joining}
                className="w-full h-12 gradient-primary font-bold"
              >
                Continue as {joinName}
              </Button>
              <Button variant="ghost" onClick={() => setEditingName(true)} className="w-full">
                Use a different name
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Your name</label>
                <Input
                  value={joinName}
                  onChange={(e) => setJoinName(e.target.value)}
                  placeholder="Your name"
                  maxLength={20}
                  autoFocus
                  className="h-12 text-base"
                  onKeyDown={(e) => e.key === "Enter" && joinName.trim() && doJoin(joinName.trim())}
                />
              </div>
              <Button
                onClick={() => doJoin(joinName.trim())}
                disabled={!joinName.trim() || joining}
                className="w-full h-12 gradient-primary font-bold"
              >
                Join Room
              </Button>
            </>
          )}
        </Card>
      </main>
    );
  }

  // Route by status & mode
  if (room.status === "lobby") {
    return <Lobby room={room} players={players} amHost={amHost} me={me} />;
  }

  if (room.mode === "retro") {
    // Retro stays on the board across all post-lobby phases
    return (
      <RetroBoard
        room={room}
        players={players}
        round={round}
        submissions={submissions}
        
        reactions={reactions}
        me={me}
        amHost={amHost}
        publishReactionSnapshot={publishReactionSnapshot}
      />
    );
  }

  if (room.status === "finished") {
    return room.mode === "poker" ? (
      <PokerSummary room={room} players={players} amHost={amHost} />
    ) : (
      <GameOver room={room} players={players} />
    );
  }

  if (room.mode === "game") {
    if (room.status === "revealing" && round) {
      return <GameReveal room={room} players={players} round={round} submissions={submissions} me={me} amHost={amHost} />;
    }
    if (round) {
      return <GameRound room={room} players={players} round={round} submissions={submissions} me={me} amHost={amHost} />;
    }
  }

  if (room.mode === "poker") {
    if (room.status === "revealing" && round) {
      return <PokerReveal room={room} players={players} round={round} submissions={submissions} me={me} amHost={amHost} />;
    }
    if (round) {
      return <PokerRound room={room} players={players} round={round} submissions={submissions} me={me} amHost={amHost} />;
    }
  }

  return <Lobby room={room} players={players} amHost={amHost} me={me} />;
};

export default RoomPage;
